import React, { useState, useEffect } from 'react';
import { X, Save, Settings, Globe, MessageSquare, FileText, Image, Volume2, Upload, User } from 'lucide-react';
import { toast } from 'react-toastify';

const NodeConfigPanel = ({ node, isOpen, onClose, onSave }) => {
  const [config, setConfig] = useState({});
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (node) {
      setConfig(node.data.config || {});
    }
  }, [node]);

  if (!isOpen || !node) return null;

  const handleSave = () => {
    onSave(node.id, config);
    toast.success('Node configuration saved!');
    onClose();
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const getIcon = (nodeType) => {
    const icons = {
      prompt: MessageSquare,
      textGen: FileText,
      visual: Image,
      worksheet: FileText,
      audio: Volume2,
      upload: Upload,
      input: User,
      api: Globe
    };
    const IconComponent = icons[nodeType] || Settings;
    return <IconComponent className="w-5 h-5" />;
  };

  const renderBasicConfig = () => {
    switch (node.type) {
      case 'prompt':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Text
              </label>
              <textarea
                value={config.prompt || ''}
                onChange={(e) => updateConfig('prompt', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="4"
                placeholder="Enter your prompt here... Use {{variable}} for dynamic content"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Variables (comma-separated)
              </label>
              <input
                type="text"
                value={config.variables?.join(', ') || ''}
                onChange={(e) => updateConfig('variables', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="topic, grade, language"
              />
            </div>
          </div>
        );

      case 'textGen':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic/Concept
              </label>
              <input
                type="text"
                value={config.concept || ''}
                onChange={(e) => updateConfig('concept', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter the topic or concept"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grade Level
                </label>
                <select
                  value={config.gradeLevel || '4'}
                  onChange={(e) => updateConfig('gradeLevel', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(grade => (
                    <option key={grade} value={grade.toString()}>Grade {grade}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={config.language || 'English'}
                  onChange={(e) => updateConfig('language', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Marathi">Marathi</option>
                  <option value="Gujarati">Gujarati</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'visual':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Visual Concept
              </label>
              <input
                type="text"
                value={config.concept || ''}
                onChange={(e) => updateConfig('concept', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe what visual to create"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Style
              </label>
              <select
                value={config.style || 'educational'}
                onChange={(e) => updateConfig('style', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="educational">Educational</option>
                <option value="diagram">Diagram</option>
                <option value="chart">Chart</option>
                <option value="illustration">Illustration</option>
              </select>
            </div>
          </div>
        );

      case 'worksheet':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic
              </label>
              <input
                type="text"
                value={config.concept || ''}
                onChange={(e) => updateConfig('concept', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter worksheet topic"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade Levels
              </label>
              <div className="flex flex-wrap gap-2">
                {[3,4,5,6,7,8].map(grade => (
                  <label key={grade} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.grades?.includes(grade.toString()) || false}
                      onChange={(e) => {
                        const currentGrades = config.grades || [];
                        if (e.target.checked) {
                          updateConfig('grades', [...currentGrades, grade.toString()]);
                        } else {
                          updateConfig('grades', currentGrades.filter(g => g !== grade.toString()));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">Grade {grade}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={config.language || 'English'}
                onChange={(e) => updateConfig('language', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Marathi">Marathi</option>
              </select>
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text to Convert
              </label>
              <textarea
                value={config.text || ''}
                onChange={(e) => updateConfig('text', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Enter text to convert to audio"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={config.language || 'English'}
                  onChange={(e) => updateConfig('language', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Marathi">Marathi</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voice
                </label>
                <select
                  value={config.voice || 'default'}
                  onChange={(e) => updateConfig('voice', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="default">Default</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'upload':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Type
              </label>
              <select
                value={config.fileType || 'image'}
                onChange={(e) => updateConfig('fileType', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="image">Image (PNG, JPG, JPEG)</option>
                <option value="pdf">PDF Document</option>
                <option value="document">Text Document</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max File Size
              </label>
              <select
                value={config.maxSize || '10MB'}
                onChange={(e) => updateConfig('maxSize', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="5MB">5 MB</option>
                <option value="10MB">10 MB</option>
                <option value="25MB">25 MB</option>
                <option value="50MB">50 MB</option>
              </select>
            </div>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> File upload requires user interaction during workflow execution. 
                The uploaded file will be processed by the connected agents.
              </p>
            </div>
          </div>
        );

      case 'input':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Type
              </label>
              <select
                value={config.inputType || 'text'}
                onChange={(e) => updateConfig('inputType', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="text">Text Input</option>
                <option value="textarea">Long Text</option>
                <option value="number">Number</option>
                <option value="select">Multiple Choice</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Placeholder Text
              </label>
              <input
                type="text"
                value={config.placeholder || ''}
                onChange={(e) => updateConfig('placeholder', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter placeholder text"
              />
            </div>
            {config.inputType === 'select' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options (comma-separated)
                </label>
                <input
                  type="text"
                  value={config.options?.join(', ') || ''}
                  onChange={(e) => updateConfig('options', e.target.value.split(',').map(opt => opt.trim()).filter(Boolean))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}
          </div>
        );

      case 'api':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Type
              </label>
              <select
                value={config.apiType || 'wikipedia'}
                onChange={(e) => updateConfig('apiType', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="wikipedia">Wikipedia Search</option>
                <option value="country">Country Information</option>
                <option value="quotes">Educational Quotes</option>
                <option value="jokes">Educational Jokes</option>
                <option value="weather">Weather Data (API Key Required)</option>
                <option value="news">News Data (API Key Required)</option>
                <option value="nasa">NASA Data (API Key Required)</option>
                <option value="custom">Custom API</option>
              </select>
            </div>
            
            {renderApiSpecificConfig()}
          </div>
        );

      default:
        return (
          <div className="text-center text-gray-500">
            No configuration options available for this node type.
          </div>
        );
    }
  };

  const renderApiSpecificConfig = () => {
    switch (config.apiType) {
      case 'wikipedia':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Query
              </label>
              <input
                type="text"
                value={config.query || ''}
                onChange={(e) => updateConfig('query', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter search term"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={config.language || 'en'}
                onChange={(e) => updateConfig('language', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="mr">Marathi</option>
              </select>
            </div>
          </>
        );

      case 'country':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country Name
            </label>
            <input
              type="text"
              value={config.countryName || ''}
              onChange={(e) => updateConfig('countryName', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter country name"
            />
          </div>
        );

      case 'weather':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={config.location || ''}
                onChange={(e) => updateConfig('location', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter city name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={config.apiKey || ''}
                onChange={(e) => updateConfig('apiKey', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter OpenWeatherMap API key"
              />
            </div>
          </>
        );

      case 'news':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic
              </label>
              <input
                type="text"
                value={config.topic || ''}
                onChange={(e) => updateConfig('topic', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter news topic"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={config.apiKey || ''}
                onChange={(e) => updateConfig('apiKey', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter NewsAPI key"
              />
            </div>
          </>
        );

      case 'quotes':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <select
              value={config.category || 'education'}
              onChange={(e) => updateConfig('category', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="education">Education</option>
              <option value="inspirational">Inspirational</option>
              <option value="motivation">Motivation</option>
              <option value="science">Science</option>
            </select>
          </div>
        );

      case 'nasa':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endpoint
              </label>
              <select
                value={config.endpoint || 'apod'}
                onChange={(e) => updateConfig('endpoint', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="apod">Astronomy Picture of the Day</option>
                <option value="earth">Earth Imagery</option>
                <option value="mars-photos">Mars Rover Photos</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <input
                type="password"
                value={config.apiKey || ''}
                onChange={(e) => updateConfig('apiKey', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter NASA API key"
              />
            </div>
          </>
        );

      case 'custom':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API URL
              </label>
              <input
                type="text"
                value={config.url || ''}
                onChange={(e) => updateConfig('url', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Method
              </label>
              <select
                value={config.method || 'GET'}
                onChange={(e) => updateConfig('method', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {getIcon(node.type)}
            <h2 className="text-xl font-semibold text-gray-900">
              Configure {node.type} Node
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderBasicConfig()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>Save Configuration</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigPanel;